import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { BatchImportRepository } from '@data-layer/repositories';
import { finalize, takeUntil } from 'rxjs/operators';
import { DocumentsService } from '@services/documents.service';
import { CompaniesService } from '@services/companies.service';
import { ReplaySubject } from 'rxjs';
import { Router } from '@angular/router';
import { ALLOWED_FILE_EXTENSIONS, ALLOWED_FILE_MIMES } from '@constants/constants';
import { CustomValidator } from '@helpers/custom-validator';
import { environment } from '@env/environment';

@Component({
  selector: 'es-batch-sidebar',
  templateUrl: './es-batch-sidebar.component.html',
  styleUrls: ['./es-batch-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsBatchSidebarComponent implements OnInit, OnDestroy {
  @Input() public batchId: number;
  @Output() public onCreated: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('link', { static: false }) public linkRef: ElementRef;

  public autoConfirmFc: UntypedFormControl;
  public uploadedERPFile: File;
  public uploadedAdditionalFiles: File[] = [null];
  public isLoading: boolean;

  public allowedAdditionalFileMimes: typeof ALLOWED_FILE_MIMES = ALLOWED_FILE_MIMES;
  public validators: any[] = [
    CustomValidator.fileExtensions(ALLOWED_FILE_EXTENSIONS),
    CustomValidator.maxFileSize(environment.files.import_file_size_limit),
  ];

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private batchImportRepository: BatchImportRepository,
    private documentsService: DocumentsService,
    private companiesService: CompaniesService,
    private cdr: ChangeDetectorRef,
    private router: Router,
  ) {}

  public ngOnInit(): void {
    this.autoConfirmFc = new UntypedFormControl(false);
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public create(): void {
    this.isLoading = true;

    this.batchImportRepository
      .importEntrySummary(
        this.companiesService.getCompany()?.id,
        [this.uploadedERPFile, ...this.uploadedAdditionalFiles.filter((file: File) => !!file)],
        this.batchId,
      )
      .pipe(
        finalize(() => (this.isLoading = false)),
        takeUntil(this.destroy$),
      )
      .subscribe((batchId: number) => {
        if (this.autoConfirmFc.value) {
          this.router.navigate(['/', 'entry-summary', 'batch', batchId, 'loading'], {
            queryParams: { isAfterUpload: true, isConfirmed: true },
          });
        } else {
          this.router.navigate(['/', 'entry-summary', 'batch', batchId, 'loading'], { queryParams: { isAfterUpload: true } });
        }

        this.onCreated.emit();
      });
  }

  public onUploadERPFile(file: File): void {
    this.uploadedERPFile = file;
  }

  public onRemoveERPFile(): void {
    this.uploadedERPFile = null;
  }

  public onUpdateAdditionalFile(files: File[]): void {
    this.uploadedAdditionalFiles = files;
  }

  public downloadSample(): void {
    this.isLoading = true;

    this.batchImportRepository
      .getEntrySummaryTemplate()
      .pipe(
        finalize(() => (this.isLoading = false)),
        takeUntil(this.destroy$),
      )
      .subscribe((file: Blob) => {
        this.documentsService.downloadBlobFile(file, 'entry_summary_template.xlsx');

        this.cdr.markForCheck();
      });
  }

  public get isAddedAdditionalDocs(): boolean {
    return this.uploadedAdditionalFiles?.length > 0 && this.uploadedAdditionalFiles?.[0] !== null;
  }
}
